import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Settings, Flag, Bell, Shield, RefreshCw, Plus, Trash2,
  Save, AlertTriangle, CheckCircle, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [systemSettings, setSystemSettings] = useState({});
  
  // New announcement form
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    type: 'info',
    active: true
  });
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);

  const adminKey = sessionStorage.getItem('aura_admin_key');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [flagsRes, announcementsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/admin/feature-flags`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/api/admin/announcements`, { headers: { 'X-Admin-Key': adminKey } }),
        axios.get(`${API}/api/admin/system-settings`, { headers: { 'X-Admin-Key': adminKey } })
      ]);
      
      setFeatureFlags(flagsRes.data.flags?.length > 0 ? flagsRes.data.flags : getDefaultFlags());
      setAnnouncements(announcementsRes.data.announcements || []);
      setSystemSettings(settingsRes.data || {});
    } catch (error) {
      // Use defaults if API not ready
      setFeatureFlags(getDefaultFlags());
      setAnnouncements([]);
      setSystemSettings({});
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFlags = () => [
    { id: 'nutrition_pro', name: 'Nutrition Pro', description: 'Advanced nutrition tracking features', enabled: true, premium: true },
    { id: 'training_matrix', name: 'Training Matrix', description: '4-week training program matrix', enabled: true, premium: false },
    { id: 'coach_assignments', name: 'Coach Assignments', description: 'Allow coaches to assign workouts', enabled: true, premium: true },
    { id: 'ai_recommendations', name: 'AI Recommendations', description: 'AI-powered workout suggestions', enabled: true, premium: false },
    { id: 'wearable_sync', name: 'Wearable Sync', description: 'Sync with fitness wearables', enabled: false, premium: true },
    { id: 'nil_dashboard', name: 'NIL Dashboard', description: 'Name, Image, Likeness tracking', enabled: true, premium: false },
    { id: 'transfer_portal', name: 'Transfer Portal', description: 'College transfer management', enabled: true, premium: false },
    { id: 'chat_support', name: 'AI Chat Support', description: 'AI-powered support chatbot', enabled: true, premium: false },
  ];

  const toggleFeatureFlag = async (flagId) => {
    const updatedFlags = featureFlags.map(flag => 
      flag.id === flagId ? { ...flag, enabled: !flag.enabled } : flag
    );
    setFeatureFlags(updatedFlags);
    
    try {
      await axios.post(`${API}/api/admin/feature-flags`, 
        { flags: updatedFlags },
        { headers: { 'X-Admin-Key': adminKey } }
      );
      toast.success('Feature flag updated');
    } catch (error) {
      toast.error('Failed to update feature flag');
    }
  };

  const createAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error('Title and message are required');
      return;
    }

    try {
      await axios.post(`${API}/api/admin/announcements`, 
        newAnnouncement,
        { headers: { 'X-Admin-Key': adminKey } }
      );
      toast.success('Announcement created');
      setShowAnnouncementDialog(false);
      setNewAnnouncement({ title: '', message: '', type: 'info', active: true });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Delete this announcement?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/announcements/${announcementId}`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success('Announcement deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const getAnnouncementIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'error': return <AlertTriangle className="w-5 h-5 text-red-400" />;
      default: return <Info className="w-5 h-5 text-blue-400" />;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
              System Settings
            </h1>
            <p className="text-aura-text-muted text-sm mt-1">
              Configure features, announcements, and system behavior
            </p>
          </div>
          <Button onClick={fetchSettings} variant="outline" className="border-white/10 text-white">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="features" className="w-full">
          <TabsList className="bg-aura-surface border border-white/10">
            <TabsTrigger value="features" className="data-[state=active]:bg-aura-primary">
              <Flag className="w-4 h-4 mr-2" />
              Feature Flags
            </TabsTrigger>
            <TabsTrigger value="announcements" className="data-[state=active]:bg-aura-primary">
              <Bell className="w-4 h-4 mr-2" />
              Announcements
            </TabsTrigger>
            <TabsTrigger value="system" className="data-[state=active]:bg-aura-primary">
              <Shield className="w-4 h-4 mr-2" />
              System
            </TabsTrigger>
          </TabsList>

          {/* Feature Flags Tab */}
          <TabsContent value="features" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase tracking-wider">
                  Feature Flags
                </CardTitle>
                <p className="text-sm text-aura-text-muted">
                  Enable or disable features globally
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {featureFlags.map((flag) => (
                  <div 
                    key={flag.id} 
                    className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        flag.enabled ? 'bg-green-500/20' : 'bg-white/10'
                      }`}>
                        <Flag className={`w-5 h-5 ${flag.enabled ? 'text-green-400' : 'text-aura-text-muted'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{flag.name}</p>
                          {flag.premium && (
                            <Badge className="bg-amber-500/20 text-amber-400 border-0 text-xs">
                              Premium
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-aura-text-muted">{flag.description}</p>
                      </div>
                    </div>
                    <Switch 
                      checked={flag.enabled}
                      onCheckedChange={() => toggleFeatureFlag(flag.id)}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-white uppercase tracking-wider">
                    Announcements
                  </CardTitle>
                  <p className="text-sm text-aura-text-muted">
                    System-wide notifications for users
                  </p>
                </div>
                <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-aura-primary hover:bg-aura-primary-hover">
                      <Plus className="w-4 h-4 mr-2" />
                      New Announcement
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-aura-surface border-white/10">
                    <DialogHeader>
                      <DialogTitle className="text-white font-heading uppercase">
                        Create Announcement
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="text-xs text-aura-text-muted uppercase">Title</label>
                        <Input
                          value={newAnnouncement.title}
                          onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                          className="bg-aura-bg border-white/10 text-white mt-1"
                          placeholder="Announcement title"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-aura-text-muted uppercase">Message</label>
                        <Textarea
                          value={newAnnouncement.message}
                          onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                          className="bg-aura-bg border-white/10 text-white mt-1"
                          placeholder="Announcement message"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-aura-text-muted uppercase">Type</label>
                        <div className="flex gap-2 mt-2">
                          {['info', 'success', 'warning', 'error'].map((type) => (
                            <Button
                              key={type}
                              type="button"
                              variant={newAnnouncement.type === type ? 'default' : 'outline'}
                              size="sm"
                              className={newAnnouncement.type === type ? '' : 'border-white/10'}
                              onClick={() => setNewAnnouncement({ ...newAnnouncement, type })}
                            >
                              {type}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <Button onClick={createAnnouncement} className="w-full bg-aura-primary">
                        <Save className="w-4 h-4 mr-2" />
                        Create Announcement
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {announcements.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
                    <p className="text-aura-text-muted">No active announcements</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {announcements.map((announcement) => (
                      <div 
                        key={announcement._id || announcement.id}
                        className="flex items-start justify-between p-4 rounded-lg bg-white/5"
                      >
                        <div className="flex items-start gap-3">
                          {getAnnouncementIcon(announcement.type)}
                          <div>
                            <p className="text-white font-medium">{announcement.title}</p>
                            <p className="text-sm text-aura-text-muted mt-1">{announcement.message}</p>
                            <p className="text-xs text-aura-text-muted mt-2">
                              {announcement.created_at && format(parseISO(announcement.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => deleteAnnouncement(announcement._id || announcement.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system" className="mt-4">
            <Card className="bg-aura-surface border-white/10">
              <CardHeader>
                <CardTitle className="font-heading text-white uppercase tracking-wider">
                  System Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Maintenance Mode */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                  <div>
                    <p className="text-white font-medium">Maintenance Mode</p>
                    <p className="text-sm text-aura-text-muted">
                      Show maintenance page to all users
                    </p>
                  </div>
                  <Switch checked={systemSettings.maintenanceMode || false} />
                </div>

                {/* Registration */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                  <div>
                    <p className="text-white font-medium">Allow New Registrations</p>
                    <p className="text-sm text-aura-text-muted">
                      Enable or disable new user signups
                    </p>
                  </div>
                  <Switch checked={systemSettings.allowRegistration !== false} />
                </div>

                {/* Beta Mode */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                  <div>
                    <p className="text-white font-medium">Beta Mode</p>
                    <p className="text-sm text-aura-text-muted">
                      Show beta badge and enable experimental features
                    </p>
                  </div>
                  <Switch checked={systemSettings.betaMode || true} />
                </div>

                {/* API Rate Limits */}
                <div className="p-4 rounded-lg bg-white/5">
                  <p className="text-white font-medium mb-2">API Configuration</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-aura-text-muted uppercase">Admin Secret</label>
                      <Input 
                        type="password" 
                        value="••••••••••••" 
                        disabled
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-aura-text-muted uppercase">Rate Limit (req/min)</label>
                      <Input 
                        type="number" 
                        defaultValue={100}
                        className="bg-aura-bg border-white/10 text-white mt-1"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
